import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GUIDE_VISIBILITY_HINTS,
  GUIDE_VISIBILITY_LABELS,
  GUIDE_VISIBILITIES,
  guideSlugSchema,
  type GuideRevision,
  type GuideStep,
  type GuideVisibility,
  type GuideWithSteps,
} from "@shared/modules/guides";
import { ApiError } from "../../../core/lib/api";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Modal } from "../../../core/components/ui/modal";
import { Badge } from "../../../core/components/ui/badge";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { guidesApi, publicGuideUrl } from "../lib/api";
import { Markdown } from "../components/markdown";
import { GuideStatusBadge, GuideVisibilityBadge } from "../components/badges";

interface GuideForm {
  title: string;
  slug: string;
  description: string;
  visibility: GuideVisibility;
}

interface StepForm {
  title: string;
  bodyMd: string;
}

function toForm(g: GuideWithSteps): GuideForm {
  return { title: g.title, slug: g.slug, description: g.description ?? "", visibility: g.visibility };
}

/** Édition d'un guide (administrateur) : /guides/:id/modifier. */
export function GuideEditor() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);

  const [guide, setGuide] = useState<GuideWithSteps | null | undefined>(undefined);
  const [form, setForm] = useState<GuideForm | null>(null);
  const [revisions, setRevisions] = useState<GuideRevision[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [step, setStep] = useState<StepForm | null>(null);
  const [stepDirty, setStepDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmStep, setConfirmStep] = useState<GuideStep | null>(null);
  const [confirmGuide, setConfirmGuide] = useState(false);

  const load = useCallback(async () => {
    const g = await guidesApi.get(id);
    setGuide(g);
    setForm((f) => f ?? toForm(g));
    return g;
  }, [id]);

  useEffect(() => {
    load()
      .then((g) => {
        // Première étape sélectionnée d'office : l'éditeur ne s'ouvre pas sur du vide.
        const first = g.steps[0];
        if (first) {
          setSelectedId(first.id);
          setStep({ title: first.title, bodyMd: first.bodyMd });
        }
      })
      .catch(() => setGuide(null));
    guidesApi
      .revisions(id)
      .then(setRevisions)
      .catch(() => setRevisions([]));
  }, [id, load]);

  const guideDirty = Boolean(
    guide &&
      form &&
      (form.title !== guide.title ||
        form.slug !== guide.slug ||
        form.description !== (guide.description ?? "") ||
        form.visibility !== guide.visibility),
  );
  const slugError = form && !guideSlugSchema.safeParse(form.slug).success ? "Minuscules, chiffres et tirets seulement." : undefined;
  const dirty = guideDirty || stepDirty;

  /** Enregistre l'étape en cours si elle a changé ; renvoie false en cas d'échec. */
  const saveStep = async (): Promise<boolean> => {
    if (!guide || !selectedId || !step || !stepDirty) return true;
    if (!step.title.trim()) {
      push("warning", "Le titre de l'étape est requis");
      return false;
    }
    try {
      await guidesApi.updateStep(guide.id, selectedId, { title: step.title.trim(), bodyMd: step.bodyMd });
      setStepDirty(false);
      return true;
    } catch {
      push("danger", "Enregistrement de l'étape impossible");
      return false;
    }
  };

  /** Enregistre le guide (métadonnées) puis l'étape courante. */
  const save = async (): Promise<boolean> => {
    if (!guide || !form || busy) return false;
    if (!form.title.trim()) {
      push("warning", "Le titre du guide est requis");
      return false;
    }
    if (slugError) {
      push("warning", "Le slug est invalide", slugError);
      return false;
    }
    setBusy(true);
    try {
      if (guideDirty) {
        await guidesApi.update(guide.id, {
          title: form.title.trim(),
          slug: form.slug,
          description: form.description.trim() ? form.description.trim() : null,
          visibility: form.visibility,
        });
      }
      const ok = await saveStep();
      if (!ok) return false;
      const fresh = await load();
      setForm(toForm(fresh));
      push("success", "Guide enregistré");
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        push("warning", "Ce slug est déjà utilisé", "Choisissez-en un autre.");
      } else {
        push("danger", "Enregistrement impossible");
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!guide || busy) return;
    if (dirty) {
      const ok = await save();
      if (!ok) return;
    }
    setBusy(true);
    try {
      const published = await guidesApi.publish(guide.id);
      await load();
      guidesApi
        .revisions(guide.id)
        .then(setRevisions)
        .catch(() => undefined);
      push("success", `Guide publié (version ${published.version})`);
    } catch {
      push("danger", "Publication impossible");
    } finally {
      setBusy(false);
    }
  };

  const selectStep = async (s: GuideStep) => {
    if (s.id === selectedId) return;
    // Changer d'étape n'oublie pas ce qui vient d'être tapé.
    const ok = await saveStep();
    if (!ok) return;
    setSelectedId(s.id);
    setStep({ title: s.title, bodyMd: s.bodyMd });
    setStepDirty(false);
  };

  const addStep = async () => {
    if (!guide || busy) return;
    const ok = await saveStep();
    if (!ok) return;
    setBusy(true);
    try {
      const created = await guidesApi.createStep(guide.id, { title: `Étape ${guide.steps.length + 1}`, bodyMd: "" });
      await load();
      setSelectedId(created.id);
      setStep({ title: created.title, bodyMd: created.bodyMd });
      setStepDirty(false);
    } catch {
      push("danger", "Ajout de l'étape impossible");
    } finally {
      setBusy(false);
    }
  };

  const move = async (s: GuideStep, delta: -1 | 1) => {
    if (!guide || busy) return;
    const ids = guide.steps.map((x) => x.id);
    const from = ids.indexOf(s.id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    ids.splice(from, 1);
    ids.splice(to, 0, s.id);
    setBusy(true);
    try {
      await guidesApi.orderSteps(guide.id, ids);
      await load();
    } catch {
      push("danger", "Déplacement impossible");
    } finally {
      setBusy(false);
    }
  };

  const removeStep = async () => {
    if (!guide || !confirmStep) return;
    setBusy(true);
    try {
      await guidesApi.deleteStep(guide.id, confirmStep.id);
      const fresh = await load();
      if (selectedId === confirmStep.id) {
        const next = fresh.steps[0] ?? null;
        setSelectedId(next?.id ?? null);
        setStep(next ? { title: next.title, bodyMd: next.bodyMd } : null);
        setStepDirty(false);
      }
      push("success", "Étape supprimée");
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setConfirmStep(null);
      setBusy(false);
    }
  };

  const removeGuide = async () => {
    if (!guide) return;
    setBusy(true);
    try {
      await guidesApi.remove(guide.id);
      push("success", "Guide supprimé");
      navigate("/guides");
    } catch {
      push("danger", "Suppression impossible");
      setBusy(false);
    }
  };

  if (guide === undefined || !form) {
    return (
      <div className="page">
        <p className="muted">Chargement…</p>
      </div>
    );
  }
  if (guide === null) {
    return (
      <div className="page">
        <PageHeader title="Guide introuvable" />
        <div className="empty">
          <Button variant="ghost" onClick={() => navigate("/guides")}>
            Retour aux guides
          </Button>
        </div>
      </div>
    );
  }

  const selected = guide.steps.find((s) => s.id === selectedId) ?? null;

  return (
    <div className="page guide-editor">
      <PageHeader
        title={guide.title}
        subtitle="Métadonnées, étapes et publication."
        actions={
          <>
            <span className="guide-meta">
              <GuideStatusBadge status={guide.status} />
              {guide.version > 0 ? <Badge variant="neutral">v{guide.version}</Badge> : null}
              {dirty ? <Badge variant="warning">Non enregistré</Badge> : null}
            </span>
            <Button variant="ghost" icon={<Icon name="eye" size={16} />} onClick={() => navigate(`/guides/${guide.slug}`)}>
              Voir
            </Button>
            <Button variant="ghost" onClick={() => void save()} disabled={busy || !dirty}>
              Enregistrer
            </Button>
            <Button variant="primary" onClick={publish} disabled={busy || guide.steps.length === 0}>
              Publier
            </Button>
          </>
        }
      />

      <div className="guide-editor__layout">
        <aside className="guide-editor__side">
          <details className="form-section" open>
            <summary className="form-section__head">
              <span className="form-section__chevron">
                <Icon name="chevron-right" size={14} />
              </span>
              <h3 className="form-section__title">Informations</h3>
              <span className="form-section__count">
                <GuideVisibilityBadge visibility={form.visibility} />
              </span>
            </summary>
            <div className="form-section__grid form-section__grid--full">
              <Field label="Titre">
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </Field>
              <Field label="Slug" hint={`Adresse : /guides/${form.slug || "…"}`} error={slugError}>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.trim().toLowerCase() })} />
              </Field>
              <Field label="Description" hint="Une ou deux phrases affichées dans la liste.">
                <Textarea
                  className="textarea--sm"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Field>
              <Field label="Visibilité" hint={GUIDE_VISIBILITY_HINTS[form.visibility]}>
                <Select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value as GuideVisibility })}>
                  {GUIDE_VISIBILITIES.map((v) => (
                    <option key={v} value={v}>
                      {GUIDE_VISIBILITY_LABELS[v]}
                    </option>
                  ))}
                </Select>
              </Field>
              {guide.visibility === "public" && guide.status === "published" ? (
                <p className="guide-editor__public">
                  Lien public :{" "}
                  <a href={publicGuideUrl(guide.slug)} target="_blank" rel="noopener noreferrer">
                    {publicGuideUrl(guide.slug)}
                  </a>
                </p>
              ) : null}
            </div>
          </details>

          <details className="form-section" open>
            <summary className="form-section__head">
              <span className="form-section__chevron">
                <Icon name="chevron-right" size={14} />
              </span>
              <h3 className="form-section__title">Étapes</h3>
              <span className="form-section__count">{guide.steps.length}</span>
            </summary>
            {guide.steps.length === 0 ? <p className="muted">Aucune étape. Ajoutez la première.</p> : null}
            <ol className="guide-editor__steps">
              {guide.steps.map((s, i) => (
                <li key={s.id} className={`guide-editor__step${s.id === selectedId ? " is-active" : ""}`}>
                  <button type="button" className="guide-editor__step-select" onClick={() => void selectStep(s)}>
                    <span className="guide-editor__step-num">{i + 1}</span>
                    <span className="guide-editor__step-title">
                      {s.id === selectedId && step ? step.title || "Sans titre" : s.title}
                    </span>
                  </button>
                  <span className="guide-editor__step-actions">
                    <button
                      type="button"
                      className="guide-editor__step-btn"
                      aria-label="Monter"
                      title="Monter"
                      disabled={busy || i === 0}
                      onClick={() => void move(s, -1)}
                    >
                      <Icon name="arrow-up" size={14} />
                    </button>
                    <button
                      type="button"
                      className="guide-editor__step-btn"
                      aria-label="Descendre"
                      title="Descendre"
                      disabled={busy || i === guide.steps.length - 1}
                      onClick={() => void move(s, 1)}
                    >
                      <Icon name="arrow-down" size={14} />
                    </button>
                    <button
                      type="button"
                      className="guide-editor__step-btn guide-editor__step-btn--danger"
                      aria-label="Supprimer"
                      title="Supprimer"
                      disabled={busy}
                      onClick={() => setConfirmStep(s)}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </span>
                </li>
              ))}
            </ol>
            <Button variant="ghost" size="sm" icon={<Icon name="plus" size={14} />} onClick={addStep} disabled={busy}>
              Ajouter une étape
            </Button>
          </details>

          {revisions.length > 0 ? (
            <details className="form-section">
              <summary className="form-section__head">
                <span className="form-section__chevron">
                  <Icon name="chevron-right" size={14} />
                </span>
                <h3 className="form-section__title">Historique des versions</h3>
                <span className="form-section__count">{revisions.length}</span>
              </summary>
              <ul className="guide-editor__revisions">
                {revisions.map((r) => (
                  <li key={r.id} className="guide-editor__revision">
                    <Badge variant="neutral">v{r.version}</Badge>
                    <span>{formatDateTime(r.createdAt, { dateStyle: "medium", timeStyle: "short" })}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <div className="guide-editor__danger">
            <Button variant="ghost-danger" size="sm" onClick={() => setConfirmGuide(true)} disabled={busy}>
              Supprimer le guide
            </Button>
          </div>
        </aside>

        <section className="guide-editor__main" aria-label="Étape en cours">
          {selected && step ? (
            <>
              <Field label="Titre de l'étape">
                <Input
                  value={step.title}
                  onChange={(e) => {
                    setStep({ ...step, title: e.target.value });
                    setStepDirty(true);
                  }}
                />
              </Field>
              <div className="guide-editor__split">
                <Field label="Contenu (markdown)" hint="Titres, listes, tableaux, images et vidéos par URL.">
                  <Textarea
                    className="guide-editor__source"
                    value={step.bodyMd}
                    onChange={(e) => {
                      setStep({ ...step, bodyMd: e.target.value });
                      setStepDirty(true);
                    }}
                    spellCheck
                  />
                </Field>
                <div className="guide-editor__preview">
                  <span className="guide-editor__preview-label">Aperçu</span>
                  <div className="guide-editor__preview-body">
                    <h2 className="guide-reader__step-title">{step.title || "Sans titre"}</h2>
                    <Markdown body={step.bodyMd} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="empty">Sélectionnez une étape ou ajoutez-en une pour commencer à rédiger.</div>
          )}
        </section>
      </div>

      <Modal
        open={confirmStep !== null}
        title="Supprimer cette étape ?"
        onClose={() => setConfirmStep(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmStep(null)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={removeStep} disabled={busy}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          « {confirmStep?.title} » sera retirée du guide. Les versions déjà publiées la conservent.
        </p>
      </Modal>

      <Modal
        open={confirmGuide}
        title="Supprimer ce guide ?"
        onClose={() => setConfirmGuide(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmGuide(false)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={removeGuide} disabled={busy}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>Le guide, ses étapes et l'historique de ses versions seront supprimés. Cette action est définitive.</p>
      </Modal>
    </div>
  );
}
